import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, throwError } from 'rxjs';

import {
  ChecklistDto,
  ChecklistDetailDto,
  ChecklistGroupDto,
  ChecklistItemDto,
  ChecklistListResponseDto,
  CreateChecklistGroupPayload,
  CreateChecklistItemPayload,
  CreateChecklistPayload,
  UpdateChecklistGroupPayload,
  UpdateChecklistItemPayload,
  UpdateChecklistPayload,
} from '../models/checklist.models';

@Injectable({ providedIn: 'root' })
export class ChecklistsApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = '/api/bhp/api/checklists';

  getChecklists(search?: string, refreshToken?: number): Observable<ChecklistListResponseDto> {
    let params = new HttpParams();

    if (search?.trim()) {
      params = params.set('search', search.trim());
    }

    if (refreshToken !== undefined) {
      params = params.set('_refresh', refreshToken);
    }

    return this.http.get<ChecklistListResponseDto>(`${this.baseUrl}/`, { params });
  }

  getChecklistsFull(search?: string): Observable<ChecklistListResponseDto<ChecklistDetailDto>> {
    let params = new HttpParams();

    if (search?.trim()) {
      params = params.set('search', search.trim());
    }

    return this.http.get<ChecklistListResponseDto<ChecklistDetailDto>>(`${this.baseUrl}/`, {
      params,
      headers: {
        'X-Group-ResultType': 'full',
      },
    });
  }

  getChecklist(checklistId: string): Observable<ChecklistDetailDto> {
    return this.http.get<ChecklistDetailDto>(`${this.baseUrl}/${checklistId}/`, {
      headers: {
        'X-Group-ResultType': 'full',
      },
    });
  }

  createChecklist(payload: CreateChecklistPayload): Observable<ChecklistDto> {
    return this.http.post<ChecklistDto>(`${this.baseUrl}/`, payload);
  }

  updateChecklist(checklistId: string, payload: UpdateChecklistPayload): Observable<ChecklistDto> {
    return this.http.patch<ChecklistDto>(`${this.baseUrl}/${checklistId}/`, payload);
  }

  cloneChecklist(checklistId: string): Observable<ChecklistDetailDto> {
    return this.http.post<ChecklistDetailDto>(`${this.baseUrl}/${checklistId}/clone/`, {});
  }

  deleteChecklist(checklistId: string): Observable<void> {
    return this.deleteWithoutJsonParsing(`${this.baseUrl}/${checklistId}/`);
  }

  createGroup(checklistId: string, payload: CreateChecklistGroupPayload): Observable<ChecklistGroupDto> {
    return this.http.post<ChecklistGroupDto>(`${this.baseUrl}/${checklistId}/groups/`, payload);
  }

  updateGroup(groupId: string, payload: UpdateChecklistGroupPayload): Observable<ChecklistGroupDto> {
    return this.http.patch<ChecklistGroupDto>(`${this.baseUrl}/groups/${groupId}/`, payload);
  }

  deleteGroup(groupId: string): Observable<void> {
    return this.deleteWithoutJsonParsing(`${this.baseUrl}/groups/${groupId}/`);
  }

  createItem(groupId: string, payload: CreateChecklistItemPayload): Observable<ChecklistItemDto> {
    return this.http.post<ChecklistItemDto>(`${this.baseUrl}/groups/${groupId}/items/`, payload);
  }

  updateItem(itemId: string, payload: UpdateChecklistItemPayload): Observable<ChecklistItemDto> {
    return this.http.patch<ChecklistItemDto>(`${this.baseUrl}/items/${itemId}/`, payload);
  }

  deleteItem(itemId: string): Observable<void> {
    return this.deleteWithoutJsonParsing(`${this.baseUrl}/items/${itemId}/`);
  }

  private deleteWithoutJsonParsing(url: string): Observable<void> {
    return this.http.delete(url, { responseType: 'text' }).pipe(
      map(() => undefined),
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status >= 200 && error.status < 300) {
          return of(undefined);
        }

        return throwError(() => error);
      }),
    );
  }
}
